import { useCallback, useEffect, useState } from 'react';
import { CalendarDays, Sparkles } from 'lucide-react';
import { ActivityShell, ActivityCrumb } from './components/ActivityShell';
import { campaignWindowText, fetchCampaigns, type CampaignSummary } from './api';

/**
 * 活动列表页（公开，免登录）。
 *
 * 数据全部来自 GET /public/campaigns：后台建一个活动，这里就出现一条，下线或过期即刻消失，
 * 前端不另存一份「活动清单」——两处维护同一件事必然漂移，而这里的正确形态只有一种：读服务端。
 */
export default function ActivityListPage() {
  const [items, setItems] = useState<CampaignSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setItems(await fetchCampaigns());
    } catch (e) {
      setError(e instanceof Error ? e.message : '活动信息加载失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <ActivityShell>
      <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
        <ActivityCrumb current="" />

        <header className="mb-10">
          <h1 className="text-2xl md:text-3xl font-bold text-slate-800 mb-3">最新活动</h1>
          <p className="text-sm text-slate-600 leading-relaxed max-w-2xl">
            进行中的活动都在这里。每条活动的参与方式、赠送内容与有效时间以它自己的页面为准，
            活动结束或名额发完即从本页撤下。
          </p>
        </header>

        {loading && <p className="text-sm text-slate-500 py-16 text-center">正在读取活动…</p>}

        {!loading && error && (
          <div className="py-16 text-center">
            <p className="text-sm text-slate-600 mb-4">{error}</p>
            <button
              type="button"
              onClick={() => void load()}
              className="text-sm font-medium px-5 py-2 rounded-full border border-teal-600 text-teal-700 hover:bg-teal-50 transition-colors"
            >
              重新加载
            </button>
          </div>
        )}

        {!loading && !error && items?.length === 0 && (
          <div className="py-16 text-center">
            <p className="text-sm text-slate-600 mb-2">当前没有进行中的活动</p>
            <p className="text-xs text-slate-500">活动会不定时开展，也可以先注册账号，新功能与活动会同步开放。</p>
          </div>
        )}

        {!loading && !error && items && items.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {items.map((c) => (
              <a
                key={c.key}
                href={`/activity/${c.key}`}
                className="group bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden flex flex-col"
              >
                {c.heroImage && (
                  <img
                    src={c.heroImage}
                    alt={c.title}
                    className="w-full h-40 object-cover bg-slate-100"
                    loading="lazy"
                  />
                )}
                <div className="p-5 flex-1 flex flex-col">
                  <h2 className="text-base font-semibold text-slate-800 mb-2 group-hover:text-teal-700 transition-colors">
                    {c.title}
                  </h2>
                  {c.subtitle && <p className="text-sm text-slate-600 leading-relaxed mb-4">{c.subtitle}</p>}
                  <div className="mt-auto space-y-2">
                    {c.benefitText && (
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-teal-50 text-teal-700">
                        <Sparkles className="h-3.5 w-3.5" />
                        {c.benefitText}
                      </span>
                    )}
                    <p className="flex items-center gap-1.5 text-xs text-slate-500">
                      <CalendarDays className="h-3.5 w-3.5" />
                      {campaignWindowText(c)}
                    </p>
                  </div>
                </div>
              </a>
            ))}
          </div>
        )}
      </div>
    </ActivityShell>
  );
}
